/**
 * Procedural deep-space backdrop.
 *
 * A single inverted sphere that follows the camera, so it reads as infinitely
 * distant no matter how far the view travels — the galaxy spans 300,000 units,
 * and any backdrop with a real position would be flown past.
 *
 * Depth testing is off and it renders first, so it is always behind everything
 * without participating in the depth buffer at all. That also sidesteps the
 * logarithmic depth buffer, which a custom shader would otherwise have to
 * implement by hand to avoid z-fighting.
 *
 * Everything is generated in the fragment shader: no textures to load, no
 * assets to ship, and it stays sharp at any zoom.
 */

import * as THREE from 'three';

const vertexShader = /* glsl */ `
  varying vec3 vDirection;

  void main() {
    // Direction from the sphere's centre, which is what the fragment shader
    // samples — position alone would swim as the sphere follows the camera.
    vDirection = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  precision highp float;

  varying vec3 vDirection;
  uniform vec3 uCoreColor;
  uniform vec3 uRimColor;
  uniform vec3 uNebulaA;
  uniform vec3 uNebulaB;

  // Cheap 3D value noise. Not the best-looking hash available, but it is
  // stable across drivers, which matters more here than absolute quality.
  float hash(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }

  float noise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);

    return mix(
      mix(mix(hash(i + vec3(0,0,0)), hash(i + vec3(1,0,0)), f.x),
          mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y),
      mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x),
          mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y),
      f.z);
  }

  // Five octaves is enough for cloud structure without the shader becoming the
  // most expensive thing on screen.
  float fbm(vec3 p) {
    float total = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 5; i++) {
      total += noise(p) * amplitude;
      p *= 2.02;
      amplitude *= 0.5;
    }
    return total;
  }

  /**
   * Stars, as isolated bright cells rather than scattered points: the domain is
   * chopped into cells, each gets one candidate star, and only a small fraction
   * of cells light up. That keeps them from clumping the way pure noise does.
   */
  float starField(vec3 dir, float density, float threshold) {
    vec3 p = dir * density;
    vec3 cell = floor(p);
    vec3 offset = fract(p) - 0.5;

    float presence = hash(cell);
    if (presence < threshold) return 0.0;

    // Jitter within the cell so stars are not on a visible lattice.
    vec3 jitter = vec3(hash(cell + 1.7), hash(cell + 4.3), hash(cell + 9.1)) - 0.5;
    float distance = length(offset - jitter * 0.7);

    float brightness = (presence - threshold) / (1.0 - threshold);
    return smoothstep(0.09, 0.0, distance) * brightness;
  }

  void main() {
    vec3 dir = normalize(vDirection);

    // Base gradient: lighter toward the galactic plane, darker at the poles,
    // so there is a sense of up even with nothing else on screen.
    float planar = 1.0 - abs(dir.z);
    vec3 color = mix(uRimColor, uCoreColor, pow(planar, 2.5));

    // Two nebula layers at different scales, the second masked by the first so
    // the colour pools instead of forming an even wash.
    float cloudA = fbm(dir * 2.1);
    float cloudB = fbm(dir * 5.3 + 11.0);

    float veil = smoothstep(0.45, 0.95, cloudA);
    color += uNebulaA * veil * 0.5;
    color += uNebulaB * smoothstep(0.5, 1.0, cloudB) * veil * 0.65;

    // Dust lanes: darken where the fine layer is thin, which breaks up the
    // clouds and stops them reading as fog.
    color *= 0.75 + 0.25 * smoothstep(0.2, 0.7, cloudB);

    // Three star layers. The rare bright ones carry a faint colour cast so the
    // field is not uniformly white.
    float faint = starField(dir, 340.0, 0.972);
    float mid = starField(dir, 160.0, 0.982);
    float bright = starField(dir, 70.0, 0.990);

    color += vec3(0.55, 0.62, 0.78) * faint * 0.5;
    color += vec3(0.85, 0.88, 1.0) * mid * 0.85;
    color += mix(vec3(1.0, 0.86, 0.7), vec3(0.78, 0.88, 1.0), hash(floor(dir * 70.0)))
             * bright * 1.4;

    gl_FragColor = vec4(color, 1.0);
  }
`;

export interface SpaceBackground {
  mesh: THREE.Mesh;
  /** Keeps the backdrop centred on the camera. Call once per frame. */
  follow: (cameraPosition: THREE.Vector3) => void;
  dispose: () => void;
}

export function createSpaceBackground(): SpaceBackground {
  const geometry = new THREE.SphereGeometry(1, 32, 24);
  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    side: THREE.BackSide,
    depthTest: false,
    depthWrite: false,
    fog: false,
    uniforms: {
      uCoreColor: { value: new THREE.Color(0x0a1020) },
      uRimColor: { value: new THREE.Color(0x03050c) },
      uNebulaA: { value: new THREE.Color(0x1d3a6b) },
      uNebulaB: { value: new THREE.Color(0x54246b) },
    },
  });

  const mesh = new THREE.Mesh(geometry, material);
  // Radius is arbitrary because it follows the camera and ignores depth; it
  // only has to sit inside the far plane.
  mesh.scale.setScalar(50_000);
  mesh.renderOrder = -1000;
  mesh.frustumCulled = false;

  return {
    mesh,
    follow: (cameraPosition) => mesh.position.copy(cameraPosition),
    dispose: () => {
      geometry.dispose();
      material.dispose();
    },
  };
}
